/**
 * DataTable<T> — Reusable, accessible, fully-controlled data table
 * Built on shadcn/ui Table + DropdownMenu + AlertDialog + Checkbox + Skeleton + Tooltip
 */

import React, { useState, useMemo, useCallback, useRef, useEffect, type ReactNode } from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import {
  MoreVertical,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Search,
  AlertCircle,
  ChevronDownIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Column<T> {
  key: keyof T | string;
  header: string | ReactNode;
  render?: (value: unknown, row: T) => ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  truncate?: boolean;
}

export interface RowAction<T> {
  label: string;
  icon?: ReactNode;
  onClick: (row: T) => void;
  variant?: "default" | "destructive";
  hidden?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  disabledReason?: string;
  requireConfirm?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
  confirmLabel?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyField: keyof T;
  pageSize?: number;
  stickyHeader?: boolean;
  selectable?: boolean;
  searchable?: boolean;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
  maxHeight?: string;
  onRowClick?: (row: T) => void;
  onSelectionChange?: (selected: T[]) => void;
  actions?: RowAction<T>[];
  renderExpanded?: (row: T) => ReactNode;
  toolbarExtra?: ReactNode;
  striped?: boolean;
  tableBorderClass?: string;
  rowBorderClass?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SortDir = "asc" | "desc";

function getCellValue<T>(row: T, key: keyof T | string): unknown {
  return (row as Record<string, unknown>)[key as string];
}

function matchesSearch<T>(row: T, columns: Column<T>[], query: string): boolean {
  const q = query.toLowerCase();
  return columns.some((col) => {
    const val = getCellValue(row, col.key);
    if (val == null) return false;
    return String(val).toLowerCase().includes(q);
  });
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Truncated cell with tooltip */
function TruncatedCell({ value }: { value: unknown }) {
  const text = value == null ? "—" : String(value);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block max-w-[200px] truncate">{text}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs break-words bg-brand-navy text-white">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

/** Confirm dialog for destructive/confirm actions */
function ConfirmActionDialog<T>({
  action,
  row,
  open,
  onOpenChange,
}: {
  action: RowAction<T>;
  row: T;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="border-brand-steel/50">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base font-semibold text-gray-900">
            {action.confirmTitle ?? "Confirm Action"}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-brand-steel">
            {action.confirmDescription ?? "Are you sure you want to proceed?"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-brand-steel/50 text-brand-steel hover:bg-brand-panel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              "font-medium text-sm text-white",
              action.variant === "destructive"
                ? "bg-brand-error hover:bg-brand-error/90"
                : "bg-brand-crimson hover:bg-brand-crimson/90"
            )}
            onClick={() => {
              action.onClick(row);
              onOpenChange(false);
            }}
          >
            {action.confirmLabel ?? "Confirm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Row actions dropdown (three-dot menu) */
function RowActionsMenu<T>({ row, actions }: { row: T; actions: RowAction<T>[] }) {
  const [confirmAction, setConfirmAction] = useState<RowAction<T> | null>(null);

  const visibleActions = actions.filter((a) => !a.hidden?.(row));
  if (visibleActions.length === 0) return null;

  const hasDestructive = visibleActions.some((a) => a.variant === "destructive");
  const destructiveIndex = visibleActions.findIndex((a) => a.variant === "destructive");

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-brand-steel hover:text-gray-700"
            aria-label="Row actions"
          >
            <MoreVertical size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-auto min-w-40 border-brand-steel/50 shadow-md text-sm">
          {visibleActions.map((action, i) => {
            const isDisabled = action.disabled?.(row) ?? false;
            const showSeparator = hasDestructive && i === destructiveIndex && destructiveIndex > 0;

            return (
              <React.Fragment key={action.label}>
                {showSeparator && <DropdownMenuSeparator />}
                {isDisabled && action.disabledReason ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <DropdownMenuItem
                          disabled
                          variant={action.variant === "destructive" ? "destructive" : "default"}
                          className="gap-2"
                        >
                          {action.icon}
                          {action.label}
                        </DropdownMenuItem>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="bg-brand-navy text-white">
                      {action.disabledReason}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <DropdownMenuItem
                    disabled={isDisabled}
                    variant={action.variant === "destructive" ? "destructive" : "default"}
                    className="gap-2 cursor-pointer"
                    onSelect={() => {
                      if (action.requireConfirm) {
                        setConfirmAction(action);
                      } else {
                        action.onClick(row);
                      }
                    }}
                  >
                    {action.icon}
                    {action.label}
                  </DropdownMenuItem>
                )}
              </React.Fragment>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {confirmAction && (
        <ConfirmActionDialog
          action={confirmAction}
          row={row}
          open={!!confirmAction}
          onOpenChange={(open) => {
            if (!open) setConfirmAction(null);
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export function DataTable<T>({
  data,
  columns,
  keyField,
  pageSize = 25,
  stickyHeader = true,
  selectable = false,
  searchable = false,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = "No records found",
  maxHeight = "600px",
  onRowClick,
  onSelectionChange,
  actions,
  renderExpanded,
  toolbarExtra,
  striped = false,
  tableBorderClass = "border-brand-steel/50",
  rowBorderClass: rowBorderClassProp,
  className,
}: DataTableProps<T>) {
  const rowBorderClass = rowBorderClassProp ?? tableBorderClass;
  // -- State
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const mainRef = useRef<HTMLDivElement>(null);

  // Reset page on search change
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // -- Derived data
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    return data.filter((row) => matchesSearch(row, columns, search));
  }, [data, columns, search]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => dir * compareValues(getCellValue(a, sortKey), getCellValue(b, sortKey)));
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const showPagination = sorted.length > pageSize;

  // -- Selection
  const allKeys = useMemo(() => new Set(data.map((r) => String(getCellValue(r, keyField)))), [data, keyField]);
  const allSelected = allKeys.size > 0 && allKeys.size === selectedKeys.size;
  const someSelected = selectedKeys.size > 0 && !allSelected;

  const toggleSelectAll = useCallback(() => {
    const next = allSelected ? new Set<string>() : new Set(allKeys);
    setSelectedKeys(next);
    onSelectionChange?.(data.filter((r) => next.has(String(getCellValue(r, keyField)))));
  }, [allSelected, allKeys, data, keyField, onSelectionChange]);

  const toggleSelectRow = useCallback(
    (row: T) => {
      const key = String(getCellValue(row, keyField));
      const next = new Set(selectedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setSelectedKeys(next);
      onSelectionChange?.(data.filter((r) => next.has(String(getCellValue(r, keyField)))));
    },
    [selectedKeys, data, keyField, onSelectionChange]
  );

  // -- Sort
  const toggleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        if (sortDir === "desc") {
          setSortKey(null);
          setSortDir("asc");
        } else {
          setSortDir("desc");
        }
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
      setCurrentPage(1);
    },
    [sortKey, sortDir]
  );

  // -- Expand
  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // -- Column count for skeletons / colspan
  const totalCols = columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0) + (renderExpanded ? 1 : 0);

  // -- Render helpers
  const renderCellContent = (col: Column<T>, row: T) => {
    const value = getCellValue(row, col.key);
    if (col.render) return col.render(value, row);
    if (value == null) return <span className="text-brand-steel/50">—</span>;
    if (col.truncate) return <TruncatedCell value={value} />;
    return String(value);
  };

  const alignClass = (align?: "left" | "center" | "right") => {
    if (align === "center") return "text-center";
    if (align === "right") return "text-right";
    return "text-left";
  };

  const SortIcon = ({ colKey }: { colKey: string }) => {
    if (sortKey !== colKey) return <ArrowUpDown size={14} className="text-brand-steel/80" />;
    return sortDir === "asc" ? (
      <ArrowUp size={14} className="text-brand-crimson" />
    ) : (
      <ArrowDown size={14} className="text-brand-crimson" />
    );
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <TooltipProvider>
      <div ref={mainRef} className={cn("flex flex-col gap-3", className)}>
        {/* Top bar: search + selection banner */}
        {(searchable || toolbarExtra || (selectable && selectedKeys.size > 0)) && (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {searchable && (
                <div className="relative max-w-xs flex-1">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-steel/50"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    className="w-full pl-9 pr-3 py-2 border border-brand-steel/50 bg-white rounded-lg text-sm text-gray-900 placeholder:text-brand-steel focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
                    aria-label="Search table"
                  />
                </div>
              )}
              {toolbarExtra}
            </div>
            {selectable && selectedKeys.size > 0 && (
              <div className="text-sm text-brand-steel">
                <span className="font-medium text-gray-900">{selectedKeys.size}</span> record
                {selectedKeys.size !== 1 ? "s" : ""} selected
              </div>
            )}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center justify-center py-16 text-center" role="alert">
            <AlertCircle size={48} className="text-brand-error/50 mb-4" />
            <h3 className="text-base font-semibold text-gray-700 mb-1">Something went wrong</h3>
            <p className="text-sm text-brand-steel/70 mb-4">{error}</p>
            {onRetry && (
              <Button
                onClick={onRetry}
                className="bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-4 py-2"
              >
                Retry
              </Button>
            )}
          </div>
        )}

        {/* Table */}
        {!error && (
          <div className={`bg-white border ${tableBorderClass} rounded-lg shadow-sm mb-4 overflow-clip`}>
            <Table containerClassName="overflow-y-auto" containerStyle={{ maxHeight }}>
              <TableHeader className="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_0_rgb(148_163_184/0.5)]">
                <TableRow className={`border-b ${tableBorderClass} hover:bg-transparent`}>
                  {/* Expand toggle header */}
                  {renderExpanded && <TableHead className="w-10 px-2" />}

                  {/* Selection header */}
                  {selectable && (
                    <TableHead className="w-10 px-4 py-3">
                      <Checkbox
                        checked={allSelected}
                        // @ts-expect-error -- indeterminate is valid on radix checkbox
                        indeterminate={someSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all records"
                      />
                    </TableHead>
                  )}

                  {/* Column headers */}
                  {columns.map((col) => (
                    <TableHead
                      key={String(col.key)}
                      className={cn(
                        "text-sm font-semibold text-brand-navy uppercase tracking-wider px-4 py-3",
                        alignClass(col.align),
                        col.sortable && "cursor-pointer select-none"
                      )}
                      style={col.width ? { width: col.width } : undefined}
                      onClick={col.sortable ? () => toggleSort(String(col.key)) : undefined}
                      aria-sort={
                        col.sortable && sortKey === String(col.key)
                          ? sortDir === "asc"
                            ? "ascending"
                            : "descending"
                          : col.sortable
                          ? "none"
                          : undefined
                      }
                    >
                      {col.sortable ? (
                        <span className="inline-flex items-center gap-1">
                          {col.header}
                          <SortIcon colKey={String(col.key)} />
                        </span>
                      ) : (
                        col.header
                      )}
                    </TableHead>
                  ))}

                  {/* Actions header */}
                  {actions && <TableHead className="w-12 px-2" />}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Loading skeletons */}
                {loading &&
                  Array.from({ length: pageSize }, (_, i) => (
                    <TableRow key={`skeleton-${i}`} className={`border-b ${rowBorderClass}`}>
                      {renderExpanded && (
                        <TableCell className="px-2">
                          <Skeleton className="h-4 w-4 bg-brand-steel/10" />
                        </TableCell>
                      )}
                      {selectable && (
                        <TableCell className="px-4 py-3">
                          <Skeleton className="h-4 w-4 bg-brand-steel/10" />
                        </TableCell>
                      )}
                      {columns.map((col) => (
                        <TableCell key={String(col.key)} className="px-4 py-3">
                          <Skeleton className="h-4 w-full bg-brand-steel/10" />
                        </TableCell>
                      ))}
                      {actions && (
                        <TableCell className="px-2">
                          <Skeleton className="h-4 w-4 bg-brand-steel/10" />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}

                {/* Empty / no results */}
                {!loading && paginated.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={totalCols} className="h-48">
                      <div className="flex flex-col items-center justify-center text-center py-8">
                        <p className="text-sm text-brand-steel/70">
                          {search.trim() ? "No results match your search" : emptyMessage}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Data rows */}
                {!loading &&
                  paginated.map((row, rowIndex) => {
                    const rowKey = String(getCellValue(row, keyField));
                    const isSelected = selectedKeys.has(rowKey);
                    const isExpanded = expandedKeys.has(rowKey);

                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow
                          className={cn(
                            `border-b ${rowBorderClass} transition-colors`,
                            onRowClick && "cursor-pointer",
                            isSelected
                              ? "bg-brand-crimson/5"
                              : striped
                              ? rowIndex % 2 === 1
                                ? "bg-brand-panel"
                                : ""
                              : "hover:bg-brand-panel/50"
                          )}
                          onClick={
                            onRowClick
                              ? (e) => {
                                  // Don't trigger row click on checkbox/button/link clicks
                                  const target = e.target as HTMLElement;
                                  if (target.closest("button, a, input, [role='menuitem']")) return;
                                  onRowClick(row);
                                }
                              : undefined
                          }
                        >
                          {/* Expand toggle */}
                          {renderExpanded && (
                            <TableCell className="px-2 w-10">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(rowKey);
                                }}
                                aria-label={isExpanded ? "Collapse row" : "Expand row"}
                                aria-expanded={isExpanded}
                              >
                                <ChevronDownIcon
                                  size={14}
                                  className={cn("transition-transform", isExpanded && "rotate-180")}
                                />
                              </Button>
                            </TableCell>
                          )}

                          {/* Selection checkbox */}
                          {selectable && (
                            <TableCell className="px-4 py-3 w-10">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleSelectRow(row)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select row ${rowKey}`}
                              />
                            </TableCell>
                          )}

                          {/* Data cells */}
                          {columns.map((col) => (
                            <TableCell
                              key={String(col.key)}
                              className={cn("px-4 py-3 text-sm text-gray-900", alignClass(col.align))}
                              style={col.width ? { width: col.width } : undefined}
                            >
                              {renderCellContent(col, row)}
                            </TableCell>
                          ))}

                          {/* Actions */}
                          {actions && (
                            <TableCell className="px-2 w-12" onClick={(e) => e.stopPropagation()}>
                              <RowActionsMenu row={row} actions={actions} />
                            </TableCell>
                          )}
                        </TableRow>

                        {/* Expanded content */}
                        {renderExpanded && isExpanded && (
                          <TableRow className="bg-brand-panel/30">
                            <TableCell colSpan={totalCols} className="px-6 py-4">
                              {renderExpanded(row)}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {!error && !loading && showPagination && (
          <div className="flex items-center justify-between px-4 mb-4">
            <p className="text-sm text-brand">
              Showing{" "}
              <span className="font-medium text-gray-900">
                {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sorted.length)}
              </span>{" "}
              of <span className="font-medium text-gray-900">{sorted.length}</span> records
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="border-brand-steel/50 text-brand-steel hover:bg-brand-panel h-8 w-8 p-0"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </Button>

              {generatePageNumbers(safePage, totalPages).map((page, i) =>
                page === "..." ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-sm text-brand-steel/50">
                    ...
                  </span>
                ) : (
                  <Button
                    key={page}
                    variant={page === safePage ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page as number)}
                    className={cn(
                      "h-8 w-8 p-0 text-sm",
                      page === safePage
                        ? "bg-brand-crimson/10 border-brand-crimson text-brand-crimson font-semibold hover:bg-brand-crimson/20"
                        : "border-brand-steel/50 text-brand-steel hover:bg-brand-panel"
                    )}
                    aria-label={`Page ${page}`}
                    aria-current={page === safePage ? "page" : undefined}
                  >
                    {page}
                  </Button>
                )
              )}

              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="border-brand-steel/50 text-brand-steel hover:bg-brand-panel h-8 w-8 p-0"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Pagination page number generator
// ---------------------------------------------------------------------------

function generatePageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | "...")[] = [1];

  if (current > 3) pages.push("...");

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push("...");

  pages.push(total);
  return pages;
}

export default DataTable;
